import { visitorCountry } from "./visitor-country";

const INDIA_RULE_MESSAGE =
	"Please enter a valid 10-digit mobile number starting with 6, 7, 8 or 9.";

//An order in India ships only to a mobile number: ten digits, the first of
//them 6 to 9. The library's own check is a length check, so a landline or a
//mistyped digit passes it, and the order is then held at the courier - paid,
//and never shipped. A leading 0 is the trunk prefix customers type out of
//habit; the library drops it from the number it hands over, so it is not held
//against them here.
export function indiaRuleError(dialCode, nationalNumber) {
	if (dialCode !== "91") {
		return "";
	}

	const digits = String(nationalNumber).replace(/\D/g, "").replace(/^0+/, "");

	return /^[6-9]\d{9}$/.test(digits) ? "" : INDIA_RULE_MESSAGE;
}

const STYLESHEET = "/funnels-sdk/iti29/css/intlTelInput.css";
const UTILS = "/funnels-sdk/iti29/js/utils.js";

//The global is only ever this library's own chunk: the legacy bundle that
//used to ship version 26 is gone from every page, so a second boot is the
//one way it can already be defined.
function loadItiLibrary() {
	if (window.intlTelInput) {
		return Promise.resolve();
	}
	return import("/funnels-sdk/iti29/js/intl-tel-input.min.js");
}

//The stylesheet comes with the widget rather than with the page: a checkout
//that forgets it renders the country dropdown raw, and nothing says so. Pages
//carry it either as a <link> or inlined into their own CSS, so ask the browser
//instead of looking for a tag.
//
//What is asked has to be specific to the version about to mount. A page can
//hold a copy of the sheet from an older library - inlined by a build, or
//linked by hand - which answers "styles are here" while missing every rule the
//new markup needs; 29 renamed the panel's class, so 26's copy leaves it with
//no panel at all. So probe the class only 29 styles.
function itiStylesPresent() {
	const probe = document.createElement("span");
	probe.className = "iti__country-selector";
	document.body.appendChild(probe);
	const styled = getComputedStyle(probe).borderRadius === "3px";
	probe.remove();

	return styled;
}

function loadItiStyles() {
	if (itiStylesPresent()) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		const link = document.createElement("link");
		link.rel = "stylesheet";
		link.href = STYLESHEET;
		//Either outcome lets the mount go ahead: an unstyled field still
		//takes a phone number, and waiting spares the visitor the flash of
		//an unstyled dropdown.
		link.addEventListener("load", resolve);
		link.addEventListener("error", resolve);
		//First in <head>, so a page restyling the widget keeps winning -
		//appended last, this sheet would outrank the page's own rules.
		document.head.insertBefore(link, document.head.firstChild);
	});
}

//The countries the offer sells in, once the CRM has said so: the phone's
//country list is cut down to them, and to one with no list to open when the
//offer sells in a single country. Taken from the visitor's IP alone, the flag
//showed +1 or +44 to Indian customers on a VPN or a roaming SIM; their Indian
//mobile number then went out with the wrong dial code, the payment provider
//refused it, and the order got no payment link at all.
let offerCountries = [];

export function restrictPhoneCountries(countries) {
	if (!Array.isArray(countries) || !countries.length) {
		return;
	}

	offerCountries = countries.map((c) => String(c.iso || "").toLowerCase()).filter(Boolean);

	if (!window.intlTelInput || !window.intlTelInput.getInstance) {
		//The widget is not up yet: it reads the list when it mounts.
		return;
	}

	document.querySelectorAll(".sdk-form input[type=tel]").forEach((input) => {
		const iti = window.intlTelInput.getInstance(input);
		if (!iti) {
			return;
		}
		//The list is an option of the widget's own, fixed when it mounts:
		//mount it again around what the customer already typed.
		const typed = input.value;
		iti.destroy();
		input.value = typed;
		mountPhoneInput(input);
	});
}

export function phoneInput() {
	const inputs = document.querySelectorAll(".sdk-form input[type=tel]");

	if (!inputs.length) {
		return;
	}

	Promise.all([loadItiLibrary(), loadItiStyles()])
		.then(() => {
			inputs.forEach((input) => mountPhoneInput(input));
		})
		.catch(() => {});
}

//29 renamed getSelectedCountryData, and now answers null where 26 answered an
//empty object. Callers only ever want the dial code, so give them something
//to read it off either way.
export function selectedCountry(iti) {
	const country = iti.getSelectedCountry ? iti.getSelectedCountry() : iti.getSelectedCountryData();

	return country || {};
}

function mountPhoneInput(input) {
	//Mounting a second widget over a live one leaves the customer typing into a
	//field wrapped twice, with two country lists fighting over the same value.
	//getInstance is the library's documented way to ask whether an input is
	//already taken, so a page that mounts its own - or a bundle that runs twice
	//- costs nothing. It cannot catch markup a page merely inherited from an
	//export: that carries the wrapper without an instance, and only unwrapping
	//the input in the page fixes it.
	if (window.intlTelInput.getInstance && window.intlTelInput.getInstance(input)) {
		return;
	}

	const declared = document.documentElement.getAttribute("data-phone-country");

	//A landing can pin the flag's country with <html data-phone-country>;
	//without it the visitor's own country is looked up.
	const lookupCountry = () =>
		declared ? Promise.resolve(declared) : visitorCountry();

	//An example number for the selected country tells the customer what to
	//type far better than the word "Phone Number" does, and it follows the
	//flag when the country changes. Aggressive because every checkout here
	//already writes a placeholder of its own, and the polite default would
	//stand down in front of it. The number arrives with the utils script, so
	//the page's own text shows for the moment before it loads.
	//
	//Left alone, the country list opens as a panel over the whole screen on a
	//phone, hiding the form the customer was in the middle of - the selector
	//mode's "AUTO" default is what picks the panel below 500px, so pin it to
	//a dropdown.
	const single = offerCountries.length === 1 ? offerCountries[0] : null;
	window.intlTelInput(input, {
		formatAsYouType: false,
		separateDialCode: true,
		strictMode: true,
		loadUtils: () => import(UTILS),
		countrySelectorMode: "DROPDOWN",
		placeholderNumberPolicy: "AGGRESSIVE",
		...(offerCountries.length ? { onlyCountries: offerCountries } : {}),
		//A single country: its flag and dial code shown, no list to open
		...(single ? { initialCountry: single, countrySelectorMode: "OFF" } : { initialCountryLookup: lookupCountry }),
	});

	//Bound once: the widget can be mounted again over the same field.
	if (input.dataset.sdkIndiaRule) {
		return;
	}
	input.dataset.sdkIndiaRule = "1";

	//Only a hint while the customer types: a value set without an input event
	//- autofill in an in-app browser - never reaches it, so the submit handler
	//asks indiaRuleError again. An empty field is left to `required`.
	const validateIndiaRule = () => {
		const localNumber = input.value.trim();
		const iti = window.intlTelInput.getInstance(input);

		input.setCustomValidity(
			localNumber && iti ? indiaRuleError(selectedCountry(iti).dialCode, localNumber) : "",
		);
	};

	//change and blur as well: a hint left standing by a number typed earlier
	//would otherwise block the submit of a good one that autofill put in
	//its place.
	["input", "change", "blur", "countrychange"].forEach((event) => {
		input.addEventListener(event, validateIndiaRule);
	});
}
